import React, { useState } from "react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { formatApiErrorDetail } from "@/lib/api";
import { hoyISO } from "@/lib/zona";

const campo = "w-full min-h-[52px] rounded-xl border border-[#E5DFD5] bg-white px-4 text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]";
const AVISOS = [["hora", "A la hora del recordatorio"], ["1h", "1 hora antes"], ["1d", "1 día antes"]];
export const CLAVES = { persona: "family_member_id", mascota: "pet_id", vivienda: "vivienda_id", vehiculo: "vehiculo_id" };

export const relacionInicial = (m) => {
  if (!m) return "";
  if (m.pet_id) return `mascota:${m.pet_id}`;
  if (m.vivienda_id) return `vivienda:${m.vivienda_id}`;
  if (m.vehiculo_id) return `vehiculo:${m.vehiculo_id}`;
  return m.family_member_id && m.family_member_id !== "fm-self" ? `persona:${m.family_member_id}` : "";
};

// "mascota:pet-1" → { family_member_id: null, pet_id: "pet-1", ... }
export const relacionPayload = (rel) => {
  const [tipo, id] = (rel || "").split(":");
  return Object.fromEntries(Object.entries(CLAVES).map(([t, campo]) => [campo, t === tipo ? id : null]));
};

export const useNombreRelacion = () => {
  const { familyMembers = [], pets = [], viviendas = [], vehiculos = [] } = useData();
  return (rel) => {
    const [tipo, id] = (rel || "").split(":");
    const x = { persona: [familyMembers, "name"], mascota: [pets, "name"], vivienda: [viviendas, "alias"], vehiculo: [vehiculos, "alias"] }[tipo];
    const item = x && x[0].find((i) => i.id === id);
    return item ? item[x[1]] : "Sin asignar";
  };
};

export const Relacionar = ({ value, onChange, testid = "recordatorio-relacion" }) => {
  const { familyMembers = [], pets = [], viviendas = [], vehiculos = [] } = useData();
  const grupos = [
    ["Familia", "persona", familyMembers.filter((f) => !f.es_titular), (f) => f.name],
    ["Mascotas", "mascota", pets, (p) => p.name],
    ["Viviendas", "vivienda", viviendas, (v) => v.alias],
    ["Vehículos", "vehiculo", vehiculos, (v) => v.alias],
  ].filter(([, , items]) => items.length);
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${campo} mt-1.5`}>
      <option value="">Sin asignar</option>
      {grupos.map(([label, tipo, items, nombre]) => (
        <optgroup key={tipo} label={label}>
          {items.map((x) => <option key={x.id} value={`${tipo}:${x.id}`}>{nombre(x)}</option>)}
        </optgroup>
      ))}
    </select>
  );
};

// Recordatorio para una fecha concreta: no se repite ni necesita documento.
export const RecordatorioPuntual = ({ matter = null, onDone, onCancel }) => {
  const { guardarRecordatorioPuntual } = useData();
  const [f, setF] = useState({
    que: matter?.title || "", fecha: matter?.due_date || "", hora: matter?.due_time || "",
    avisos: matter?.aviso_puntual || [], rel: relacionInicial(matter),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (k) => set("avisos", f.avisos.includes(k) ? f.avisos.filter((x) => x !== k) : [...f.avisos, k]);

  const guardar = async () => {
    if (!f.que.trim()) return toast.error("Escribe qué quieres recordar.");
    if (!f.fecha) return toast.error("Elige la fecha.");
    const [tipo, id] = f.rel.split(":");
    const payload = { que: f.que.trim(), fecha: f.fecha, hora: f.hora || "", avisos: f.hora ? f.avisos : f.avisos.filter((a) => a !== "1h") };
    if (tipo) payload[CLAVES[tipo]] = id;
    setBusy(true);
    try {
      const r = await guardarRecordatorioPuntual(matter?.id, payload);
      toast.success(matter ? "Recordatorio actualizado" : "Recordatorio guardado", { description: `${r.title} · ${r.due_label}` });
      onDone(r);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el recordatorio.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="recordatorio-form">
      <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">{matter ? "Editar recordatorio" : "Recordatorio"}</h2>
      <p className="text-base text-[#6E7A74] mt-1">Para una fecha concreta. No se repite.</p>
      <div className="space-y-4 mt-5">
        <label className="block"><span className="text-base font-semibold text-[#1C2421]">Qué quieres recordar</span>
          <input data-testid="recordatorio-que" value={f.que} onChange={(e) => set("que", e.target.value)} maxLength={150}
            placeholder="Revisión de Cody en el veterinario" className={`${campo} mt-1.5`} /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-base font-semibold text-[#1C2421]">Fecha</span>
            <input data-testid="recordatorio-fecha" type="date" min={hoyISO()} value={f.fecha} onChange={(e) => set("fecha", e.target.value)} className={`${campo} mt-1.5`} /></label>
          <label className="block"><span className="text-base font-semibold text-[#1C2421]">Hora <span className="font-normal text-[#9AA39D]">(opcional)</span></span>
            <input data-testid="recordatorio-hora" type="time" value={f.hora} onChange={(e) => set("hora", e.target.value)} className={`${campo} mt-1.5`} /></label>
        </div>
        <div>
          <p className="text-base font-semibold text-[#1C2421]">Aviso previo <span className="font-normal text-[#9AA39D]">(opcional)</span></p>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {AVISOS.filter(([k]) => k !== "1h" || f.hora).map(([k, l]) => (
              <button key={k} type="button" data-testid={`recordatorio-aviso-${k}`} onClick={() => toggle(k)}
                className={`min-h-[44px] px-3 rounded-xl text-sm font-semibold border ${f.avisos.includes(k) ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5]"}`}>{l}</button>
            ))}
          </div>
          <p className="text-sm text-[#6E7A74] mt-1.5">{f.hora ? "" : "Sin hora, el aviso llega a las 9:00. "}Aparecerá en Próximos aunque no elijas aviso. Si tienes activados los avisos en el móvil, también te llegará allí.</p>
        </div>
        <label className="block"><span className="text-base font-semibold text-[#1C2421]">Relacionar con <span className="font-normal text-[#9AA39D]">(opcional)</span></span>
          <Relacionar value={f.rel} onChange={(v) => set("rel", v)} /></label>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <button data-testid="recordatorio-cancelar" onClick={onCancel} className="min-h-[52px] rounded-xl border border-[#E5DFD5] bg-white font-semibold">Cancelar</button>
        <button data-testid="recordatorio-guardar" disabled={busy} onClick={guardar} className="min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold disabled:opacity-60">{busy ? "Guardando…" : "Guardar"}</button>
      </div>
    </div>
  );
};
